import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import i18n from 'scripts/i18n/i18n';

import connect from 'components/lib/connect';
import { ITEM } from './feed/feed_item_types';
import { isItemFromOthers } from './use_manage_agent_read';
import JumpToItem from 'actions/customer/feed/jump_to_item';

const OPEN_DELAY = 100;
const CLOSE_DELAY = 5000;

/**
 * UnreadMessagesBanner will display that new messages exist when an agent:
 *   1. Navigates to a customer page with a "New Activity" line present in the feed
 *   2. Is scrolled up in the feed and new messages come in
 *
 * It opens once per position of the New Activity line, identified by the item right after the line, so entries
 * added above the line don't reopen it.
 */
export class UnreadMessagesBanner extends React.Component {
  constructor(props) {
    super(props);

    const lineItemId = getLineItemId(props.items, props.unreadLineIndex);
    this.state = {
      isVisible: !!lineItemId,
      shownLineItemId: lineItemId,
    };

    if (lineItemId && props.latestItemSeenIndex > props.unreadLineIndex) {
      this.queueClose(lineItemId);
    }

    this.onClickClose = this.onClickClose.bind(this);
    this.onClickJump = this.onClickJump.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const lineItemId = getLineItemId(nextProps.items, nextProps.unreadLineIndex);

    if (!lineItemId) {
      this.clearTimeouts();
      // The line can disappear without the agent dismissing the banner, e.g. a filter hides its item, so let it
      // reopen if the line comes back.
      const isDismissed = this.state.shownLineItemId === this.dismissedLineItemId;
      if (this.state.isVisible || (this.state.shownLineItemId && !isDismissed)) {
        this.setState({ isVisible: false, shownLineItemId: isDismissed ? this.state.shownLineItemId : null });
      }
      return;
    }

    if (lineItemId !== this.state.shownLineItemId) {
      this.clearTimeouts();
      this.openTimeout = this.props.setTimeout(() => {
        this.setState({
          isVisible: true,
        });
      }, OPEN_DELAY);
      this.setState({ shownLineItemId: lineItemId });
    }

    if (nextProps.latestItemSeenIndex > nextProps.unreadLineIndex) {
      this.queueClose(lineItemId);
    }
  }

  queueClose(lineItemId) {
    if (this.closingLineItemId === lineItemId) {
      return;
    }

    this.props.clearTimeout(this.closeTimeout);
    this.closingLineItemId = lineItemId;
    this.closeTimeout = this.props.setTimeout(() => {
      this.dismissedLineItemId = lineItemId;
      this.setState({
        isVisible: false,
      });
    }, CLOSE_DELAY);
  }

  clearTimeouts() {
    this.props.clearTimeout(this.closeTimeout);
    this.props.clearTimeout(this.openTimeout);
    this.closingLineItemId = null;
  }

  componentWillUnmount() {
    this.clearTimeouts();
  }

  render() {
    if (!this.state.isVisible || !getLineItemId(this.props.items, this.props.unreadLineIndex)) {
      return null;
    }

    let numUnread = 0;
    for (let i = this.props.unreadLineIndex; i < this.props.items.length; i++) {
      if (isItemFromOthers(this.props.items[i])) {
        numUnread += 1;
      }
    }

    if (numUnread === 0) {
      return null;
    }

    let since = '';
    const lastReadItem = findLastReadItem(this.props.items, this.props.unreadLineIndex);
    if (lastReadItem && lastReadItem.timestamp) {
      const readToMoment = moment(lastReadItem.timestamp);
      const readToTime = readToMoment.format('h:mm A');
      const now = moment();
      const readToDate = now.startOf('day').isAfter(readToMoment) ? ` on ${readToMoment.format('MMMM Do')}` : '';
      const readToYear = now.startOf('year').isAfter(readToMoment) ? `, ${readToMoment.format('YYYY')}` : '';
      since = ` since ${readToTime}${readToDate}${readToYear}`;
    }

    return (
      <div className="unreadMessagesBanner">
        <div className="unreadMessagesBanner-message" onClick={this.onClickJump}>
          {i18n.t('{{count}} new item{{since}}', { count: numUnread, since })}
        </div>
        <div className="unreadMessagesBanner-close" data-aid="unreadMessagesBanner-close" onClick={this.onClickClose}>
          <FontAwesomeIcon icon={faTimes} />
        </div>
      </div>
    );
  }

  onClickJump() {
    const nextUnreadItemIndex = findNextUnreadItemIndex(this.props.items, this.props.unreadLineIndex);
    if (this.props.items.length > nextUnreadItemIndex) {
      this.props.onClickJump(this.props.items[nextUnreadItemIndex].id);
    }
  }

  onClickClose() {
    this.clearTimeouts();
    this.dismissedLineItemId = this.state.shownLineItemId;
    this.setState({ isVisible: false });
  }
}

function getLineItemId(items, unreadLineIndex) {
  if (unreadLineIndex == null || unreadLineIndex < 0 || !items || !items[unreadLineIndex]) {
    return null;
  }
  return items[unreadLineIndex].id;
}

function findNextUnreadItemIndex(items, unreadLineIndex) {
  for (let i = unreadLineIndex; i < items.length; i++) {
    if (items[i].type === ITEM) {
      return i;
    }
  }
  return null;
}

function findLastReadItemIndex(items, unreadLineIndex) {
  for (let i = unreadLineIndex - 1; i >= 0; i--) {
    if (items[i].type === ITEM) {
      return i;
    }
  }
  return null;
}

function findLastReadItem(items, unreadLineIndex) {
  const index = findLastReadItemIndex(items, unreadLineIndex);
  return index != null ? items[index] : null;
}

UnreadMessagesBanner.propTypes = {
  items: PropTypes.array,
  latestItemSeenIndex: PropTypes.number,
  onClickJump: PropTypes.func,
  unreadLineIndex: PropTypes.number,

  clearTimeout: PropTypes.func.isRequired,
  setTimeout: PropTypes.func.isRequired,
};

UnreadMessagesBanner.defaultProps = {
  clearTimeout: window.clearTimeout.bind(window),
  setTimeout: window.setTimeout.bind(window),
};

const UnreadMessagesBannerContainer = connect(undefined, mapExecuteToProps)(UnreadMessagesBanner);

function mapExecuteToProps(executeAction) {
  return {
    onClickJump: id => executeAction(JumpToItem, id),
  };
}

UnreadMessagesBannerContainer.propTypes = {
  items: PropTypes.array,
  latestItemSeenIndex: PropTypes.number,
  unreadLineIndex: PropTypes.number,
};

export default UnreadMessagesBannerContainer;
